'use client';

import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Button } from '~/components/Button';
import { CenteredModal } from '~/components/CenteredModal';
import { Divider } from '~/components/Divider';
import { OverlayDrawer } from '~/components/Drawer';
import { useInteractiveLoader } from '~/components/PartnerLoader/PartnerLoader';
import { RadioButton, RadioButtonGroup } from '~/components/RadioButtonGroup';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { useIsTripUpdating } from '~/hooks/useIsTripUpdating';
import { useToast } from '~/hooks/useToasts';
import { api } from '~/libs/api';
import type { UpdateTripLegTimeFailureReason } from '~/services/engine-ts';
import { trackFunnelEvent } from '~/utils/funnelTracking';
import { logError } from '~/utils/logger';
import { type LegTimeOption, legTimeOptionKey } from './alternativeLegTimes';
import styles from './ChangeTravelTimesSheet.module.css';
import { invalidateTransportQueries } from './transportQueryCache';
type ChangeTravelTimesSheetProps = {
  isOpen: boolean;
  close: () => void;
  tripId: string;
  itemId: number;
  startDate: string;
  origin: string;
  destination: string;
  options: LegTimeOption[];
  currentTime: LegTimeOption | null;
  requestOnly: boolean;
};
const formatLegDate = (dateStr: string) => {
  try {
    return format(parseISO(dateStr), 'd MMM yyyy');
  } catch {
    return dateStr;
  }
};
export const ChangeTravelTimesSheet = ({
  isOpen,
  close,
  tripId,
  itemId,
  startDate,
  origin,
  destination,
  options,
  currentTime,
  requestOnly
}: ChangeTravelTimesSheetProps) => {
  const t = useTranslations('journey');
  const isDesktop = useIsDesktop();
  const toast = useToast();
  const utils = api.useUtils();
  const updateLegTime = api.trip.updateLegTime.useMutation();
  const isTripUpdating = useIsTripUpdating();
  const {
    startLoader,
    completeProgressBar
  } = useInteractiveLoader();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const closeAndResetSelection = () => {
    setSelectedKey(null);
    close();
  };
  const currentKey = currentTime ? legTimeOptionKey(currentTime) : null;
  const effectiveKey = selectedKey ?? currentKey;
  const selectedOption = options.find(option => legTimeOptionKey(option) === effectiveKey);
  const hasChange = effectiveKey !== null && effectiveKey !== currentKey;
  const failureMessage = (failureReason?: UpdateTripLegTimeFailureReason | null) => {
    if (failureReason === 'TIME_NO_LONGER_AVAILABLE') return t('transport_change_times_error_unavailable');
    if (failureReason === 'LEG_NOT_CHANGEABLE') return t('transport_change_times_error_not_changeable');
    return t('transport_change_times_error');
  };
  const showError = (failureReason_0?: UpdateTripLegTimeFailureReason | null) => toast?.add({
    title: failureMessage(failureReason_0),
    variant: 'error',
    position: 'middle'
  }, {
    timeout: 5000
  });
  const confirmTimeChange = async () => {
    if (!selectedOption || isTripUpdating) return;
    trackFunnelEvent('trip_funnel_times_confirm', {
      departure_time: selectedOption.departureTime,
      arrival_time: selectedOption.arrivalTime
    });
    startLoader();
    try {
      const {
        success,
        failureReason: failureReason_1
      } = await updateLegTime.mutateAsync({
        tripId,
        itemId,
        times: {
          ...selectedOption,
          requestOnly
        }
      });
      if (!success) {
        showError(failureReason_1);
        return;
      }
      setIsApplying(true);
      await invalidateTransportQueries(utils, tripId);
      closeAndResetSelection();
      toast?.add({
        title: t('transport_change_times_success'),
        variant: 'success',
        position: 'middle'
      }, {
        timeout: 5000
      });
    } catch {
      logError('Error changing transport leg time');
      showError();
    } finally {
      setIsApplying(false);
      completeProgressBar();
    }
  };
  const ariaLabel = t('transport_change_times_aria', {
    origin,
    destination
  });
  const body = <div className={clsx(styles.body, isDesktop && styles.bodyModal)}>
      <div className={styles.header}>
        <h2 className={styles.heading}>{t('transport_change_times_heading')}</h2>
        <p className={styles.date}>{formatLegDate(startDate)}</p>
      </div>

      <Divider variant="horizontal" />

      <div className={styles.options}>
        <RadioButtonGroup ariaLabel={t('transport_change_times_options_aria')}>
          {options.map(option_0 => {
          const key = legTimeOptionKey(option_0);
          return <RadioButton key={key} id={`transport-leg-${itemId}-${key}`} name={`transport-leg-${itemId}-times`} value={key} checked={key === effectiveKey} onChange={() => setSelectedKey(key)}>
                {t('transport_change_times_option', {
              departureTime: option_0.departureTime,
              arrivalTime: option_0.arrivalTime
            })}
              </RadioButton>;
        })}
        </RadioButtonGroup>
      </div>

      {isDesktop ? <div className={styles.footerModal}>
          <Divider variant="horizontal" />
          <div className={styles.footerModalButton}>
            <Button variant="primary" isPillShape onPress={confirmTimeChange} isDisabled={!hasChange || isTripUpdating} isLoading={updateLegTime.isPending || isApplying} dataTestId="ConfirmTravelTime">
              {t('transport_change_times_confirm')}
            </Button>
          </div>
        </div> : <Button variant="primary" size="fullWidth" isPillShape onPress={confirmTimeChange} isDisabled={!hasChange || isTripUpdating} isLoading={updateLegTime.isPending || isApplying} dataTestId="ConfirmTravelTime">
          {t('transport_change_times_confirm')}
        </Button>}
    </div>;
  return isDesktop ? <CenteredModal ariaLabel={ariaLabel} close={closeAndResetSelection} closeDataTestId="ChangeTravelTimesModalClose" dataTestId="ChangeTravelTimesModal" fitContent isOpen={isOpen} size="medium">
      {body}
    </CenteredModal> : <OverlayDrawer isOpen={isOpen} close={closeAndResetSelection} ariaLabel={ariaLabel} dataTestId="ChangeTravelTimesDrawer">
      {body}
    </OverlayDrawer>;
};